import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../api/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GraphSubscription {
  subscriptionId: string;
  expiresAt:      string;
  active:         boolean;
}

interface MailboxConfig {
  id:                string;
  name:              string;
  userEmail:         string;
  domainId:          string | null;
  useOwnCredentials: boolean;
  tenantId:          string | null;
  clientId:          string | null;
  clientSecret:      string | null;
  mailFolder:        string;
  maxEmailsPerRun:   number;
  enabled:           boolean;
  notificationMode:  string;
  lastRunAt:         string | null;
  lastError:         string | null;
  createdAt:         string;
  subscription:      GraphSubscription | null;
  aiAnalysisEnabled: boolean;
  replyEnabled:      boolean;
  forwardEnabled:    boolean;
  composeEnabled:    boolean;
}

interface MailboxDomain {
  id:                string;
  name:              string;
  domain:            string;
  tenantId:          string | null;
  clientId:          string | null;
  clientSecret:      string | null;
  mailFolder:        string;
  maxEmailsPerRun:   number;
  enabled:           boolean;
  aiAnalysisEnabled: boolean;
  replyEnabled:      boolean;
  forwardEnabled:    boolean;
  composeEnabled:    boolean;
  createdAt:         string;
  mailboxes:         MailboxConfig[];
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const IconEdit = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IconTrash = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);
const IconPlay = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
);
const IconChevron = ({ open }: { open: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
    style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .15s' }}>
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const IconWebhook = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2"/>
    <path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06"/>
    <path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8"/>
  </svg>
);
const IconDomain = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function expiresInDays(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const h = Math.floor(diff / 3600000);
  if (h < 24) return `${h}h left`;
  return `${Math.floor(h / 24)}d left`;
}

// ─── Domain form ──────────────────────────────────────────────────────────────

const emptyDomainForm = {
  name:              '',
  domain:            '',
  tenantId:          '',
  clientId:          '',
  clientSecret:      '',
  mailFolder:        'inbox',
  maxEmailsPerRun:   50,
  aiAnalysisEnabled: true,
  replyEnabled:      true,
  forwardEnabled:    true,
  composeEnabled:    true,
};

interface DomainFormProps {
  mode:     'add' | 'edit';
  initial:  typeof emptyDomainForm & { id?: string };
  onSave:   (data: typeof emptyDomainForm & { id?: string }) => Promise<void>;
  onCancel: () => void;
  saving:   boolean;
}

const DomainForm: React.FC<DomainFormProps> = ({ mode, initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial); }, [initial]);

  const set = (key: keyof typeof emptyDomainForm, value: string | number | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--card)', borderRadius: '4px', width: '100%', maxWidth: 520, boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>

        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>
              {mode === 'add' ? 'Add Domain' : 'Edit Domain'}
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--text-3)' }}>
              Organisation-level configuration shared across all mailboxes in this domain
            </p>
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: '18px', lineHeight: 1, padding: '4px', borderRadius: '4px' }}>×</button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, id: (initial as any).id }); }}>
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '58vh', overflowY: 'auto' }}>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Display Name *</label>
                <input className="input-base" required value={form.name} placeholder="Acme Corp" onChange={(e) => set('name', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Domain *</label>
                <input className="input-base" required value={form.domain} placeholder="acme.com" disabled={mode === 'edit'} onChange={(e) => set('domain', e.target.value)} style={mode === 'edit' ? { opacity: 0.6 } : {}} />
              </div>
            </div>

            <div style={{ background: 'var(--bg)', borderRadius: '4px', padding: '14px', border: '1px solid var(--border)' }}>
              <p style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-2)' }}>
                Azure AD Credentials{' '}
                <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(shared across all mailboxes in this domain)</span>
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Tenant ID</label>
                  <input className="input-base" value={form.tenantId} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" onChange={(e) => set('tenantId', e.target.value)} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Client ID</label>
                    <input className="input-base" value={form.clientId} placeholder="App registration ID" onChange={(e) => set('clientId', e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Client Secret</label>
                    <input className="input-base" type="password" value={form.clientSecret} placeholder={mode === 'edit' ? '(unchanged)' : 'App secret'} onChange={(e) => set('clientSecret', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Default Mail Folder</label>
                <input className="input-base" value={form.mailFolder} placeholder="inbox" onChange={(e) => set('mailFolder', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Default Max Emails / Run</label>
                <input className="input-base" type="number" min={1} max={500} value={form.maxEmailsPerRun} onChange={(e) => set('maxEmailsPerRun', parseInt(e.target.value) || 50)} />
              </div>
            </div>

          </div>

          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '8px', background: 'var(--bg-subtle)' }}>
            <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" style={saving ? { opacity: 0.65 } : {}}>
              {saving ? 'Saving…' : mode === 'add' ? 'Add Domain' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Mailbox form ─────────────────────────────────────────────────────────────

const emptyMailboxForm = {
  name:              '',
  userEmail:         '',
  domainId:          '',
  useOwnCredentials: false,
  tenantId:          '',
  clientId:          '',
  clientSecret:      '',
  mailFolder:        'inbox',
  maxEmailsPerRun:   50,
  aiAnalysisEnabled: true,
  replyEnabled:      true,
  forwardEnabled:    true,
  composeEnabled:    true,
};

interface MailboxFormProps {
  mode:     'add' | 'edit';
  initial:  typeof emptyMailboxForm & { id?: string };
  domains:  MailboxDomain[];
  onSave:   (data: typeof emptyMailboxForm & { id?: string }) => Promise<void>;
  onCancel: () => void;
  saving:   boolean;
}

const MailboxForm: React.FC<MailboxFormProps> = ({ mode, initial, domains, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial); }, [initial]);

  const set = (key: keyof typeof emptyMailboxForm, value: string | number | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));

  const hasDomain   = !!form.domainId;
  const showOwnCreds = !hasDomain || form.useOwnCredentials;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--card)', borderRadius: '4px', width: '100%', maxWidth: 540, boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>

        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>
              {mode === 'add' ? 'Add Mailbox' : 'Edit Mailbox'}
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--text-3)' }}>
              Configure a user mailbox — credentials inherit from the selected domain unless overridden
            </p>
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: '18px', lineHeight: 1, padding: '4px', borderRadius: '4px' }}>×</button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, id: (initial as any).id }); }}>
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '62vh', overflowY: 'auto' }}>

            {/* Domain selector */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Domain</label>
              <select
                className="input-base"
                value={form.domainId}
                onChange={(e) => {
                  set('domainId', e.target.value);
                  if (!e.target.value) set('useOwnCredentials', false);
                }}
                style={{ width: '100%' }}
              >
                <option value="">— Ungrouped (no domain) —</option>
                {domains.map((d) => (
                  <option key={d.id} value={d.id}>{d.name} ({d.domain})</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Display Name *</label>
                <input className="input-base" required value={form.name} placeholder="Claims Inbox" onChange={(e) => set('name', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Mailbox Email *</label>
                <input className="input-base" required type="email" value={form.userEmail} placeholder="claims@company.com" disabled={mode === 'edit'} onChange={(e) => set('userEmail', e.target.value)} style={mode === 'edit' ? { opacity: 0.6 } : {}} />
              </div>
            </div>

            {/* Credentials section */}
            <div style={{ background: 'var(--bg)', borderRadius: '4px', padding: '14px', border: '1px solid var(--border)' }}>
              {hasDomain ? (
                <>
                  {/* Toggle: use own creds vs inherit domain creds */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: form.useOwnCredentials ? '12px' : 0 }}>
                    <div
                      onClick={() => set('useOwnCredentials', !form.useOwnCredentials)}
                      style={{ width: 36, height: 20, borderRadius: 10, flexShrink: 0, background: form.useOwnCredentials ? 'var(--accent)' : 'var(--border-strong)', position: 'relative', transition: 'background .15s', cursor: 'pointer' }}
                    >
                      <div style={{ position: 'absolute', top: 2, left: form.useOwnCredentials ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>Use own Azure AD credentials</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                        {form.useOwnCredentials ? 'This mailbox will use its own credentials instead of the domain.' : 'Inheriting credentials from domain configuration.'}
                      </div>
                    </div>
                  </label>
                  {!form.useOwnCredentials && (
                    <div style={{ fontSize: '12px', color: 'var(--text-3)', padding: '8px 10px', background: 'var(--accent-light)', borderRadius: '4px', border: '1px solid #BDD9F5' }}>
                      Credentials are inherited from the domain. Toggle the switch above to override them for this mailbox.
                    </div>
                  )}
                </>
              ) : (
                <p style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-2)' }}>
                  Azure AD Credentials{' '}
                  <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(leave blank to use global env config)</span>
                </p>
              )}

              {showOwnCreds && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: hasDomain ? '0' : '0' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Tenant ID</label>
                    <input className="input-base" value={form.tenantId} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" onChange={(e) => set('tenantId', e.target.value)} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Client ID</label>
                      <input className="input-base" value={form.clientId} placeholder="App registration ID" onChange={(e) => set('clientId', e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Client Secret</label>
                      <input className="input-base" type="password" value={form.clientSecret} placeholder={mode === 'edit' ? '(unchanged)' : 'App secret'} onChange={(e) => set('clientSecret', e.target.value)} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Mail Folder</label>
                <input className="input-base" value={form.mailFolder} placeholder="inbox" onChange={(e) => set('mailFolder', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px', display: 'block' }}>Max Emails / Run</label>
                <input className="input-base" type="number" min={1} max={500} value={form.maxEmailsPerRun} onChange={(e) => set('maxEmailsPerRun', parseInt(e.target.value) || 50)} />
              </div>
            </div>

            <div style={{ background: 'var(--bg)', borderRadius: '4px', padding: '14px', border: '1px solid var(--border)' }}>
              <p style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-2)' }}>Feature Flags</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {([
                  { key: 'aiAnalysisEnabled', label: 'AI Analysis',  desc: 'Run GPT analysis on incoming emails' },
                  { key: 'replyEnabled',       label: 'Reply',        desc: 'Allow reply / reply-all actions' },
                  { key: 'forwardEnabled',     label: 'Forward',      desc: 'Allow forwarding emails' },
                  { key: 'composeEnabled',     label: 'Compose',      desc: 'Allow composing new emails' },
                ] as const).map(({ key, label, desc }) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '8px 10px', borderRadius: '4px', border: `1px solid ${form[key] ? 'var(--accent-light)' : 'var(--border)'}`, background: form[key] ? 'var(--accent-light)' : 'var(--card)' }}>
                    <div
                      onClick={() => set(key, !form[key])}
                      style={{ width: 36, height: 20, borderRadius: 10, flexShrink: 0, background: form[key] ? 'var(--accent)' : 'var(--border-strong)', position: 'relative', transition: 'background .15s', cursor: 'pointer' }}
                    >
                      <div style={{ position: 'absolute', top: 2, left: form[key] ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>{label}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>{desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

          </div>

          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '8px', background: 'var(--bg-subtle)' }}>
            <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" style={saving ? { opacity: 0.65 } : {}}>
              {saving ? 'Saving…' : mode === 'add' ? 'Add Mailbox' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Webhook cell ─────────────────────────────────────────────────────────────

const WebhookCell: React.FC<{
  mailbox:       MailboxConfig;
  onSubscribe:   (id: string) => void;
  onUnsubscribe: (id: string) => void;
  busy:          boolean;
}> = ({ mailbox, onSubscribe, onUnsubscribe, busy }) => {
  const isWebhook = mailbox.notificationMode === 'WEBHOOK';
  const sub       = mailbox.subscription;

  if (isWebhook && sub) {
    const expiring = sub.expiresAt && new Date(sub.expiresAt) < new Date(Date.now() + 86400000);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '2px', fontSize: '11px', fontWeight: 600, background: 'var(--purple-light)', color: 'var(--purple)' }}>
          <IconWebhook /> Webhook
        </span>
        <span style={{ fontSize: '11px', color: expiring ? 'var(--warning)' : 'var(--text-3)' }} title={fmtDateTime(sub.expiresAt)}>
          {expiresInDays(sub.expiresAt)}
        </span>
        <button onClick={() => onUnsubscribe(mailbox.id)} disabled={busy}
          style={{ padding: '2px 8px', fontSize: '11px', fontWeight: 600, borderRadius: '2px', border: '1px solid var(--danger-light)', background: 'var(--danger-light)', color: 'var(--danger)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}>
          {busy ? '…' : 'Disable'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '2px', fontSize: '11px', fontWeight: 600, background: 'var(--bg)', color: 'var(--text-2)', border: '1px solid var(--border)' }}>
        Polling
      </span>
      <button onClick={() => onSubscribe(mailbox.id)} disabled={busy || !mailbox.enabled}
        style={{ padding: '2px 8px', fontSize: '11px', fontWeight: 600, borderRadius: '2px', border: '1px solid var(--accent-light)', background: 'var(--accent-light)', color: 'var(--accent)', cursor: (busy || !mailbox.enabled) ? 'default' : 'pointer', opacity: (busy || !mailbox.enabled) ? 0.4 : 1 }}
        title={!mailbox.enabled ? 'Enable this mailbox first' : 'Enable real-time webhook mode'}>
        {busy ? '…' : 'Enable Webhook'}
      </button>
    </div>
  );
};

// ─── Mailbox table row ────────────────────────────────────────────────────────

const MailboxRow: React.FC<{
  m:             MailboxConfig;
  runningId:     string | null;
  webhookBusyId: string | null;
  onToggle:      (id: string) => void;
  onEdit:        (m: MailboxConfig) => void;
  onDelete:      (id: string, email: string) => void;
  onRun:         (id: string) => void;
  onSubscribe:   (id: string) => void;
  onUnsubscribe: (id: string) => void;
}> = ({ m, runningId, webhookBusyId, onToggle, onEdit, onDelete, onRun, onSubscribe, onUnsubscribe }) => (
  <tr style={{ opacity: m.enabled ? 1 : 0.6 }}>
    <td>
      <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)' }}>{m.name}</div>
      <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '1px' }}>{m.userEmail}</div>
      {m.useOwnCredentials && (
        <div style={{ fontSize: '11px', color: 'var(--warning)', marginTop: '1px' }}>Own credentials</div>
      )}
    </td>
    <td>
      <span style={{ padding: '2px 8px', borderRadius: '2px', background: 'var(--accent-light)', color: 'var(--accent)', fontSize: '12px', fontWeight: 600 }}>
        {m.mailFolder}
      </span>
      <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>max {m.maxEmailsPerRun}/run</div>
    </td>
    <td>
      <WebhookCell mailbox={m} onSubscribe={onSubscribe} onUnsubscribe={onUnsubscribe} busy={webhookBusyId === m.id} />
    </td>
    <td>
      <div style={{ fontSize: '12.5px', color: 'var(--text-2)' }} title={fmtDateTime(m.lastRunAt)}>
        {relativeTime(m.lastRunAt)}
      </div>
      {m.lastError && (
        <div style={{ fontSize: '11px', color: 'var(--danger)', marginTop: '2px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.lastError}>
          {m.lastError}
        </div>
      )}
    </td>
    <td>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
        {([
          { key: 'aiAnalysisEnabled', label: 'AI' },
          { key: 'replyEnabled',      label: 'Reply' },
          { key: 'forwardEnabled',    label: 'Fwd' },
          { key: 'composeEnabled',    label: 'Compose' },
        ] as const).map(({ key, label }) => (
          <span key={key} style={{ padding: '1px 6px', borderRadius: '2px', fontSize: '11px', fontWeight: 600, background: m[key] ? 'var(--success-light)' : 'var(--bg)', color: m[key] ? 'var(--success)' : 'var(--text-3)', border: `1px solid ${m[key] ? '#A7F3BF' : 'var(--border)'}` }}>
            {label}
          </span>
        ))}
      </div>
    </td>
    <td>
      <button onClick={() => onToggle(m.id)} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '2px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, background: m.enabled ? 'var(--success-light)' : 'var(--bg)', color: m.enabled ? 'var(--success)' : 'var(--text-3)' }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.enabled ? 'var(--success)' : 'var(--text-3)', display: 'inline-block' }} />
        {m.enabled ? 'Active' : 'Disabled'}
      </button>
    </td>
    <td>
      <div style={{ display: 'flex', gap: '4px' }}>
        <button onClick={() => onRun(m.id)} disabled={!m.enabled || runningId === m.id} title="Run extraction now" className="btn-cmd" style={{ color: 'var(--accent)', opacity: m.enabled && runningId !== m.id ? 1 : 0.4 }}>
          <IconPlay /> {runningId === m.id ? '…' : 'Run'}
        </button>
        <button onClick={() => onEdit(m)} title="Edit" className="btn-cmd"><IconEdit /></button>
        <button onClick={() => onDelete(m.id, m.userEmail)} title="Delete" className="btn-cmd" style={{ color: 'var(--danger)' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--danger-light)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
          <IconTrash />
        </button>
      </div>
    </td>
  </tr>
);

// ─── Mailbox table ────────────────────────────────────────────────────────────

const TABLE_HEADERS = ['Mailbox', 'Folder', 'Notifications', 'Last Run', 'Features', 'Status', 'Actions'];

const MailboxTable: React.FC<{
  mailboxes:     MailboxConfig[];
  runningId:     string | null;
  webhookBusyId: string | null;
  onToggle:      (id: string) => void;
  onEdit:        (m: MailboxConfig) => void;
  onDelete:      (id: string, email: string) => void;
  onRun:         (id: string) => void;
  onSubscribe:   (id: string) => void;
  onUnsubscribe: (id: string) => void;
}> = (props) => (
  <table className="ms-table">
    <thead>
      <tr>{TABLE_HEADERS.map((h) => <th key={h}>{h}</th>)}</tr>
    </thead>
    <tbody>
      {props.mailboxes.map((m) => <MailboxRow key={m.id} m={m} {...props} />)}
    </tbody>
  </table>
);

// ─── Domain section ───────────────────────────────────────────────────────────

const DomainSection: React.FC<{
  domain:        MailboxDomain;
  expanded:      boolean;
  onToggleOpen:  (id: string) => void;
  onEditDomain:  (d: MailboxDomain) => void;
  onDeleteDomain:(id: string, name: string) => void;
  onAddMailbox:  (domainId: string) => void;
  runningId:     string | null;
  webhookBusyId: string | null;
  onMailboxToggle:    (id: string) => void;
  onMailboxEdit:      (m: MailboxConfig) => void;
  onMailboxDelete:    (id: string, email: string) => void;
  onMailboxRun:       (id: string) => void;
  onMailboxSubscribe:   (id: string) => void;
  onMailboxUnsubscribe: (id: string) => void;
}> = ({ domain, expanded, onToggleOpen, onEditDomain, onDeleteDomain, onAddMailbox, runningId, webhookBusyId, onMailboxToggle, onMailboxEdit, onMailboxDelete, onMailboxRun, onMailboxSubscribe, onMailboxUnsubscribe }) => {
  const hasOrgCreds = !!(domain.tenantId || domain.clientId);

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden', background: 'var(--card)' }}>
      {/* Domain header */}
      <div
        onClick={() => onToggleOpen(domain.id)}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', cursor: 'pointer', userSelect: 'none', background: 'var(--bg-subtle)', borderBottom: expanded ? '1px solid var(--border)' : 'none' }}
      >
        <span style={{ color: 'var(--text-3)', display: 'flex', flexShrink: 0 }}><IconChevron open={expanded} /></span>
        <span style={{ color: 'var(--accent)', display: 'flex', flexShrink: 0 }}><IconDomain /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text)' }}>{domain.name}</span>
          <span style={{ marginLeft: 8, fontSize: '12px', color: 'var(--text-3)' }}>@{domain.domain}</span>
          <span style={{ marginLeft: 12, padding: '1px 8px', borderRadius: '2px', fontSize: '11px', fontWeight: 600, background: hasOrgCreds ? 'var(--success-light)' : 'var(--bg)', color: hasOrgCreds ? 'var(--success)' : 'var(--text-3)', border: `1px solid ${hasOrgCreds ? '#A7F3BF' : 'var(--border)'}` }}>
            {hasOrgCreds ? 'Azure AD configured' : 'No Azure AD credentials'}
          </span>
          <span style={{ marginLeft: 8, fontSize: '12px', color: 'var(--text-3)' }}>
            {domain.mailboxes.length} mailbox{domain.mailboxes.length !== 1 ? 'es' : ''}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
          <button onClick={() => onAddMailbox(domain.id)} title="Add mailbox to this domain" className="btn-cmd" style={{ color: 'var(--accent)', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <IconPlus /> Add Mailbox
          </button>
          <button onClick={() => onEditDomain(domain)} title="Edit domain" className="btn-cmd"><IconEdit /></button>
          <button
            onClick={() => onDeleteDomain(domain.id, domain.name)}
            title="Delete domain"
            className="btn-cmd"
            style={{ color: 'var(--danger)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--danger-light)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <IconTrash />
          </button>
        </div>
      </div>

      {/* Mailboxes table */}
      {expanded && (
        domain.mailboxes.length === 0 ? (
          <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: '13px' }}>
            No mailboxes in this domain yet.{' '}
            <button onClick={() => onAddMailbox(domain.id)} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontWeight: 600, fontSize: '13px', padding: 0 }}>
              Add one
            </button>
          </div>
        ) : (
          <MailboxTable
            mailboxes={domain.mailboxes}
            runningId={runningId}
            webhookBusyId={webhookBusyId}
            onToggle={onMailboxToggle}
            onEdit={onMailboxEdit}
            onDelete={onMailboxDelete}
            onRun={onMailboxRun}
            onSubscribe={onMailboxSubscribe}
            onUnsubscribe={onMailboxUnsubscribe}
          />
        )
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

type DomainFormMode  = { kind: 'add' } | { kind: 'edit'; domain: MailboxDomain } | null;
type MailboxFormMode = { kind: 'add'; domainId?: string } | { kind: 'edit'; mailbox: MailboxConfig } | null;

const Mailboxes: React.FC = () => {
  const [domains,    setDomains]    = useState<MailboxDomain[]>([]);
  const [ungrouped,  setUngrouped]  = useState<MailboxConfig[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [expanded,   setExpanded]   = useState<Set<string>>(new Set());

  const [domainFormMode,  setDomainFormMode]  = useState<DomainFormMode>(null);
  const [domainFormInit,  setDomainFormInit]  = useState<typeof emptyDomainForm & { id?: string }>(emptyDomainForm);
  const [mailboxFormMode, setMailboxFormMode] = useState<MailboxFormMode>(null);
  const [mailboxFormInit, setMailboxFormInit] = useState<typeof emptyMailboxForm & { id?: string }>(emptyMailboxForm);

  const [saving,        setSaving]        = useState(false);
  const [runningId,     setRunningId]     = useState<string | null>(null);
  const [webhookBusyId, setWebhookBusyId] = useState<string | null>(null);
  const [toast,         setToast]         = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Data loading ──

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { domains: ds, ungrouped: ug } = await apiGet<{ domains: MailboxDomain[]; ungrouped: MailboxConfig[] }>('/api/domains');
      setDomains(ds);
      setUngrouped(ug);
      // Auto-expand single domain
      if (ds.length === 1) setExpanded(new Set([ds[0].id]));
    } catch {
      showToast('Failed to load mailbox configuration', false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Domain actions ──

  const handleDomainSave = async (data: typeof emptyDomainForm & { id?: string }) => {
    setSaving(true);
    try {
      if (domainFormMode?.kind === 'add') {
        const created = await apiPost<MailboxDomain>('/api/domains', data);
        setDomains((prev) => [...prev, created]);
        setExpanded((prev) => new Set([...prev, created.id]));
        showToast('Domain added');
      } else if (data.id) {
        const updated = await apiPut<MailboxDomain>(`/api/domains/${data.id}`, data);
        setDomains((prev) => prev.map((d) => (d.id === data.id ? { ...updated, mailboxes: d.mailboxes } : d)));
        showToast('Domain updated');
      }
      setDomainFormMode(null);
    } catch (err: any) {
      showToast(err?.message || 'Save failed', false);
    } finally { setSaving(false); }
  };

  const handleDomainDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete domain "${name}"? Mailboxes under it will become ungrouped.`)) return;
    try {
      await apiDelete(`/api/domains/${id}`);
      setDomains((prev) => prev.filter((d) => d.id !== id));
      showToast('Domain deleted');
      await load(); // Reload to capture newly ungrouped mailboxes
    } catch { showToast('Delete failed', false); }
  };

  const openAddDomain = () => { setDomainFormInit(emptyDomainForm); setDomainFormMode({ kind: 'add' }); };
  const openEditDomain = (d: MailboxDomain) => {
    setDomainFormInit({ id: d.id, name: d.name, domain: d.domain, tenantId: d.tenantId || '', clientId: d.clientId || '', clientSecret: '', mailFolder: d.mailFolder, maxEmailsPerRun: d.maxEmailsPerRun, aiAnalysisEnabled: d.aiAnalysisEnabled, replyEnabled: d.replyEnabled, forwardEnabled: d.forwardEnabled, composeEnabled: d.composeEnabled });
    setDomainFormMode({ kind: 'edit', domain: d });
  };

  // ── Mailbox helpers (update local state without reload) ──

  const patchMailboxInState = (updated: MailboxConfig) => {
    setDomains((prev) => prev.map((d) => ({
      ...d,
      mailboxes: d.mailboxes.map((m) => (m.id === updated.id ? updated : m)),
    })));
    setUngrouped((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
  };

  const removeMailboxFromState = (id: string) => {
    setDomains((prev) => prev.map((d) => ({ ...d, mailboxes: d.mailboxes.filter((m) => m.id !== id) })));
    setUngrouped((prev) => prev.filter((m) => m.id !== id));
  };

  // ── Mailbox actions ──

  const handleMailboxSave = async (data: typeof emptyMailboxForm & { id?: string }) => {
    setSaving(true);
    try {
      if (mailboxFormMode?.kind === 'add') {
        const created = await apiPost<MailboxConfig>('/api/mailboxes', data);
        if (created.domainId) {
          setDomains((prev) => prev.map((d) => d.id === created.domainId ? { ...d, mailboxes: [...d.mailboxes, created] } : d));
        } else {
          setUngrouped((prev) => [...prev, created]);
        }
        showToast('Mailbox added');
      } else if (data.id) {
        const updated = await apiPut<MailboxConfig>(`/api/mailboxes/${data.id}`, data);
        // If domain assignment changed, reload everything to place it correctly
        const prev = [...domains.flatMap((d) => d.mailboxes), ...ungrouped].find((m) => m.id === data.id);
        if (prev?.domainId !== updated.domainId) {
          await load();
        } else {
          patchMailboxInState(updated);
        }
        showToast('Mailbox updated');
      }
      setMailboxFormMode(null);
    } catch (err: any) {
      showToast(err?.message || 'Save failed', false);
    } finally { setSaving(false); }
  };

  const handleMailboxToggle = async (id: string) => {
    try {
      const updated = await apiPatch<MailboxConfig>(`/api/mailboxes/${id}/toggle`, {});
      patchMailboxInState(updated);
      showToast(`Mailbox ${updated.enabled ? 'enabled' : 'disabled'}`);
    } catch { showToast('Toggle failed', false); }
  };

  const handleMailboxDelete = async (id: string, email: string) => {
    if (!window.confirm(`Delete mailbox "${email}"? Extracted emails are kept.`)) return;
    try {
      await apiDelete(`/api/mailboxes/${id}`);
      removeMailboxFromState(id);
      showToast('Mailbox deleted');
    } catch { showToast('Delete failed', false); }
  };

  const handleMailboxRun = async (id: string) => {
    setRunningId(id);
    try {
      await apiPost(`/api/mailboxes/${id}/run`, {});
      showToast('Extraction started');
    } catch { showToast('Failed to start extraction', false); }
    finally { setTimeout(() => setRunningId(null), 2000); }
  };

  const handleSubscribe = async (id: string) => {
    setWebhookBusyId(id);
    try {
      const updated = await apiPost<MailboxConfig>(`/api/mailboxes/${id}/subscribe`, {});
      patchMailboxInState(updated);
      showToast('Webhook subscription created');
    } catch (err: any) {
      showToast(err?.message || 'Failed to create webhook', false);
    } finally { setWebhookBusyId(null); }
  };

  const handleUnsubscribe = async (id: string) => {
    setWebhookBusyId(id);
    try {
      const updated = await apiDelete<MailboxConfig>(`/api/mailboxes/${id}/subscribe`);
      patchMailboxInState(updated);
      showToast('Webhook disabled — reverted to polling');
    } catch { showToast('Failed to disable webhook', false); }
    finally { setWebhookBusyId(null); }
  };

  const openAddMailbox = (domainId?: string) => {
    setMailboxFormInit({ ...emptyMailboxForm, domainId: domainId || '' });
    setMailboxFormMode({ kind: 'add', domainId });
  };

  const openEditMailbox = (m: MailboxConfig) => {
    setMailboxFormInit({ id: m.id, name: m.name, userEmail: m.userEmail, domainId: m.domainId || '', useOwnCredentials: m.useOwnCredentials, tenantId: m.tenantId || '', clientId: m.clientId || '', clientSecret: '', mailFolder: m.mailFolder, maxEmailsPerRun: m.maxEmailsPerRun, aiAnalysisEnabled: m.aiAnalysisEnabled, replyEnabled: m.replyEnabled, forwardEnabled: m.forwardEnabled, composeEnabled: m.composeEnabled });
    setMailboxFormMode({ kind: 'edit', mailbox: m });
  };

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const totalMailboxes = domains.reduce((s, d) => s + d.mailboxes.length, 0) + ungrouped.length;
  const activeMailboxes = [...domains.flatMap((d) => d.mailboxes), ...ungrouped].filter((m) => m.enabled).length;
  const webhookCount    = [...domains.flatMap((d) => d.mailboxes), ...ungrouped].filter((m) => m.notificationMode === 'WEBHOOK').length;

  const mailboxTableProps = {
    runningId, webhookBusyId,
    onToggle:      handleMailboxToggle,
    onEdit:        openEditMailbox,
    onDelete:      handleMailboxDelete,
    onRun:         handleMailboxRun,
    onSubscribe:   handleSubscribe,
    onUnsubscribe: handleUnsubscribe,
  };

  return (
    <div style={{ height: '100vh', overflowY: 'auto', boxSizing: 'border-box', background: 'var(--bg)' }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 20, zIndex: 2000, padding: '9px 16px', borderRadius: '4px', background: toast.ok ? 'var(--success-light)' : 'var(--danger-light)', color: toast.ok ? 'var(--success)' : 'var(--danger)', fontWeight: 600, fontSize: '13px', boxShadow: 'var(--shadow)', animation: 'fadeUp .18s ease', border: `1px solid ${toast.ok ? '#A7F3BF' : '#F9BDBD'}` }}>
          {toast.msg}
        </div>
      )}

      {/* Page header */}
      <div style={{ padding: '0 28px', height: 48, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--card)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: 'var(--text-3)', display: 'flex' }}><IconDomain /></span>
          <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text)' }}>Mailboxes</span>
          <span style={{ fontSize: '12px', color: 'var(--text-3)', marginLeft: 4 }}>
            {domains.length} domain{domains.length !== 1 ? 's' : ''} · {totalMailboxes} mailboxes · {activeMailboxes} active · {webhookCount} webhook
          </span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={openAddDomain} className="btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <IconPlus /> Add Domain
          </button>
          <button onClick={() => openAddMailbox()} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <IconPlus /> Add Mailbox
          </button>
        </div>
      </div>

      <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

        {loading ? (
          <div style={{ padding: '64px', textAlign: 'center', color: 'var(--text-3)' }}>
            <div style={{ width: 24, height: 24, border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto 12px' }} />
            Loading…
          </div>
        ) : domains.length === 0 && ungrouped.length === 0 ? (
          <div className="card" style={{ padding: '64px 32px', textAlign: 'center' }}>
            <span style={{ display: 'block', marginBottom: 12, color: 'var(--border-strong)' }}><IconDomain /></span>
            <p style={{ color: 'var(--text-2)', fontWeight: 600, marginBottom: '4px' }}>No domains or mailboxes configured</p>
            <p style={{ color: 'var(--text-3)', fontSize: '13px' }}>Start by adding a domain, then add mailboxes under it.</p>
          </div>
        ) : (
          <>
            {/* Domain sections */}
            {domains.map((d) => (
              <DomainSection
                key={d.id}
                domain={d}
                expanded={expanded.has(d.id)}
                onToggleOpen={toggleExpanded}
                onEditDomain={openEditDomain}
                onDeleteDomain={handleDomainDelete}
                onAddMailbox={openAddMailbox}
                {...mailboxTableProps}
                onMailboxToggle={handleMailboxToggle}
                onMailboxEdit={openEditMailbox}
                onMailboxDelete={handleMailboxDelete}
                onMailboxRun={handleMailboxRun}
                onMailboxSubscribe={handleSubscribe}
                onMailboxUnsubscribe={handleUnsubscribe}
              />
            ))}

            {/* Ungrouped mailboxes */}
            {ungrouped.length > 0 && (
              <div style={{ border: '1px dashed var(--border)', borderRadius: '6px', overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)' }}>Ungrouped Mailboxes</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>({ungrouped.length} without a domain)</span>
                </div>
                <MailboxTable mailboxes={ungrouped} {...mailboxTableProps} />
              </div>
            )}
          </>
        )}

        {/* Info */}
        <div style={{ marginTop: '4px', padding: '12px 16px', borderRadius: '4px', background: 'var(--info-light)', border: '1px solid #BDD9F5', fontSize: '12px', color: '#004578', lineHeight: 1.7 }}>
          <strong>Domain credentials</strong> are shared by all mailboxes in that domain unless a mailbox enables <em>Use own credentials</em>.{' '}
          <strong>Webhook mode</strong> requires <code>WEBHOOK_BASE_URL</code> set to your app's public URL.
        </div>

      </div>

      {/* Domain form modal */}
      {domainFormMode && (
        <DomainForm
          mode={domainFormMode.kind}
          initial={domainFormInit}
          onSave={handleDomainSave}
          onCancel={() => setDomainFormMode(null)}
          saving={saving}
        />
      )}

      {/* Mailbox form modal */}
      {mailboxFormMode && (
        <MailboxForm
          mode={mailboxFormMode.kind}
          initial={mailboxFormInit}
          domains={domains}
          onSave={handleMailboxSave}
          onCancel={() => setMailboxFormMode(null)}
          saving={saving}
        />
      )}
    </div>
  );
};

export default Mailboxes;
